import { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import StadiumHero from "../components/homepage/stadiumScene/StadiumHero";
import { MessageCircle } from "../components/icons";
import {
  WHATSAPP_CONTACT_NUMBER,
  buildWhatsAppContactUrl,
} from "../lib/whatsappContact";

// Public marketing homepage (EPIC-38). Rendered at "/" for unauthenticated
// visitors only — see the `HomeRoute` gate in `src/App.tsx`, which is the
// component that decides whether an unauthenticated visitor, a pending auth
// check, or a signed-in user (redirected to /dashboard or /guest) is what
// actually reaches this route. This component itself carries no auth logic
// and assumes it is only ever mounted for a signed-out visitor.
//
// Copy lives in the `home` i18n namespace (public/locales/{en,es}/home.json)
// — no hard-coded strings (brief 7). `data-testid`s are a stable contract:
// `home-page-routing.spec.ts` and PMA-PUB-020's E2E rely on `homepage` and
// `home-hero-stage`; keep them stable across future copy edits.
//
// Deliberately says WHAT the product gives a coach and WHY it's different,
// never the aggregation rules, rating weights, or analytics formulas — see
// the design spec's "Content" section.
export default function Home() {
  const { t } = useTranslation("home");
  const year = new Date().getFullYear();
  const pageRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // The hero stage fills the visible area below the sticky header. The header
  // height is content-driven (no fixed token), so publish its measured height
  // as --home-header-h on the page wrapper; the stage's min-height reads it.
  useLayoutEffect(() => {
    const page = pageRef.current;
    const header = headerRef.current;
    if (!page || !header) return undefined;
    const publish = () =>
      page.style.setProperty(
        "--home-header-h",
        `${Math.round(header.getBoundingClientRect().height)}px`,
      );
    publish();
    if (typeof ResizeObserver === "undefined") return undefined;
    const obs = new ResizeObserver(publish);
    obs.observe(header);
    return () => obs.disconnect();
  }, []);
  // `null` until the user supplies a real number (see whatsappContact.ts's
  // own doc comment) — every render site below treats `null` as "do not
  // render this CTA," never as a broken/disabled button.
  const whatsappUrl = buildWhatsAppContactUrl(
    WHATSAPP_CONTACT_NUMBER,
    t("whatsapp.prefilledMessage"),
  );

  return (
    <div
      ref={pageRef}
      data-testid="homepage"
      className="min-h-screen bg-surface-dark-page text-slate-100"
    >
      <header
        ref={headerRef}
        data-testid="home-header"
        className="sticky top-0 z-40 border-b border-surface-dark-border/60 bg-surface-dark-page/90 backdrop-blur"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-3 py-4 sm:px-6">
          {/*
            One labelled logo (EPIC-105 / PMA-PUB-023.AC1): both parts are crops
            of the single lightweight asset promatch-header-logo.svg (EPIC-106:
            promatch-brand-on-dark.svg, same viewBox 501 x 395.3 and geometry,
            with its embedded rasters trimmed to the two crops and re-encoded),
            scaled by --u (px per viewBox unit). Crest crop: x 165 y 40 w 172
            h 130 (balls whole; the arc top is y 44.5, the balls end at ~165,
            the wordmark band starts at 190.5). Wordmark crop: x 54 y 186 w 392
            h 52 (lettering x 55.5..445.5, y 190.5..234.5).
          */}
          <div
            role="img"
            aria-label="ProMatch Analytics"
            data-testid="home-header-logo"
            className="flex items-center gap-1.5 [--u:0.37px] max-[389px]:[--u:0.31px] sm:gap-3 sm:[--u:0.45px]"
          >
            <span
              data-testid="home-header-logo-crest"
              data-crop="165 40 172 130"
              aria-hidden="true"
              className="relative block shrink-0 overflow-hidden"
              style={{
                width: "calc(var(--u) * 172)",
                height: "calc(var(--u) * 130)",
              }}
            >
              <img
                src="/logos/promatch-header-logo.svg"
                alt=""
                aria-hidden="true"
                className="absolute max-w-none"
                style={{
                  width: "calc(var(--u) * 501)",
                  left: "calc(var(--u) * -165)",
                  top: "calc(var(--u) * -40)",
                }}
              />
            </span>
            <span
              data-testid="home-header-logo-wordmark"
              data-crop="54 186 392 52"
              aria-hidden="true"
              className="relative block shrink-0 overflow-hidden"
              style={{
                width: "calc(var(--u) * 392)",
                height: "calc(var(--u) * 52)",
              }}
            >
              <img
                src="/logos/promatch-header-logo.svg"
                alt=""
                aria-hidden="true"
                className="absolute max-w-none"
                style={{
                  width: "calc(var(--u) * 501)",
                  left: "calc(var(--u) * -54)",
                  top: "calc(var(--u) * -186)",
                }}
              />
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher variant="dark" touchTarget />
            <Link
              to="/login"
              data-testid="home-header-login-link"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center whitespace-nowrap rounded-lg border border-slate-600 px-2.5 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-slate-400 hover:text-white sm:px-4"
            >
              {t("nav.login")}
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/*
          Full-bleed hero stage (EPIC-105 / PMA-PUB-023): the Three.js night
          stadium fills the whole stage behind the hero copy. The copy column
          keeps its max-w-3xl width and sits above the scene (z-40); the
          section is pointer-events-none so taps that miss its links and
          buttons reach the scene, and each link/button re-enables events.
        */}
        <div
          data-testid="home-hero-stage"
          className="relative isolate w-full overflow-hidden min-h-[max(560px,calc(100svh-var(--home-header-h,70px)))] max-sm:min-h-0 portrait:min-h-0"
        >
          <StadiumHero />
          <section
            data-testid="home-hero"
            className="pointer-events-none relative z-40 mx-auto max-w-3xl px-6 py-24 text-center max-sm:pb-6 sm:py-32 portrait:pb-6"
          >
            <p className="text-sm font-bold uppercase tracking-[0.14em] text-teal-400">
              {t("hero.eyebrow")}
            </p>
            <h1 className="mt-4 font-condensed text-5xl font-bold leading-[1.05] text-white sm:text-6xl">
              {t("hero.headline")}
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-lg text-slate-300">
              {t("hero.subhead")}
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/login"
                data-testid="home-hero-cta-login"
                className="pointer-events-auto rounded-lg bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100"
              >
                {t("hero.ctaLogin")}
              </Link>
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-hero-cta-whatsapp"
                  className="pointer-events-auto flex items-center gap-2 rounded-lg border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-slate-400 hover:text-white"
                >
                  <MessageCircle size={16} />
                  {t("whatsapp.ctaLabel")}
                </a>
              )}
            </div>
            <a
              href="#home-explainer"
              data-testid="home-hero-cta-see-how"
              className="pointer-events-auto mt-6 inline-flex min-h-[44px] items-center text-sm font-medium text-slate-400 underline underline-offset-4 hover:text-slate-200"
            >
              {t("hero.ctaSeeHow")}
            </a>
          </section>
        </div>

        <section
          id="home-explainer"
          data-testid="home-explainer"
          className="mx-auto max-w-6xl px-6 pb-10 pt-12 sm:pt-24 portrait:pt-12"
        >
          <p className="text-center text-sm font-bold uppercase tracking-[0.14em] text-teal-400">
            {t("explainer.eyebrow")}
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {(
              ["liveLogging", "tacticalAnalysis", "shareWithTeam"] as const
            ).map((key) => (
              <div
                key={key}
                data-testid={`home-explainer-card-${key}`}
                className="rounded-xl border border-surface-dark-border bg-surface-dark-card p-6"
              >
                <h3 className="font-condensed text-xl font-bold text-slate-100">
                  {t(`explainer.cards.${key}.title`)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  {t(`explainer.cards.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section
          data-testid="home-closing-cta"
          className="mx-auto max-w-2xl px-6 pb-20 pt-12 text-center"
        >
          <h2 className="font-condensed text-3xl font-bold text-white">
            {t("closingCta.heading")}
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/login"
              data-testid="home-closing-cta-login"
              className="rounded-lg bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100"
            >
              {t("closingCta.button")}
            </Link>
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-closing-cta-whatsapp"
                className="flex items-center gap-2 rounded-lg border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-slate-400 hover:text-white"
              >
                <MessageCircle size={16} />
                {t("whatsapp.ctaLabel")}
              </a>
            )}
          </div>
        </section>
      </main>

      <footer
        data-testid="home-footer"
        className="border-t border-surface-dark-border/60"
      >
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 py-8 text-sm text-slate-400 sm:flex-row">
          <div className="flex items-center gap-3">
            <img
              src="/logos/promatch-icon.png"
              alt=""
              aria-hidden="true"
              className="h-5 w-auto"
            />
            <span>{t("footer.tagline")}</span>
          </div>
          <div className="flex items-center gap-4">
            <span>{t("footer.rights", { year })}</span>
            <Link
              to="/login"
              data-testid="home-footer-login-link"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center font-semibold text-slate-300 hover:text-white"
            >
              {t("footer.loginLink")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
